import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import InlineAlertBanner from '@sharedCR/common/InlineAlertBanner';
import DrawerCDS, { DrawerSize } from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { copyToClipboard } from 'utils/copyToClipboard';

import { showMessageAction } from 'store/global/actions';

import MersPreviewPayload from './components/MersPreviewPayload';
import { MERS_PREVIEW_FIELDS_TABLE_CONFIG } from './MersPreviewDrawer.config';
import {
  CLOSE_LABEL,
  COPY_PAYLOAD_LABEL,
  EMPTY_PREVIEW_LIST,
  mersPreviewPropType,
  NO_PREVIEW,
  NOTHING_SENT_SUBTITLE,
  PAYLOAD_COPIED_MESSAGE,
} from './MersPreviewDrawer.constants';
import { drawerBodyStyles, errorListStyles } from './MersPreviewDrawer.styles';
import { MersPreviewDrawerTestIds } from './MersPreviewDrawer.testIds';
import {
  getMersPreviewContextLine,
  getMersPreviewErrorItems,
  getMersPreviewFieldRows,
  getMersPreviewRunKey,
  getMersPreviewTitle,
  getMersPreviewVerdictConfig,
} from './MersPreviewDrawer.utils';

/**
 * Read-only drawer for a Preview MERS run (LoanActionResult.MersPreview): what the real MERS action would send for this
 * loan, and whether MERS would take it. Nothing has been sent, so the footer only copies the payload and closes. The
 * host keeps the last preview after closing, so the drawer slides out still showing it.
 */
const MersPreviewDrawer = ({ open, preview = null, onClose }) => {
  const dispatch = useDispatch();

  const {
    operation = null,
    verdict = null,
    message = '',
    environment = null,
    channel = null,
    orgId = null,
    errors = EMPTY_PREVIEW_LIST,
    fields = EMPTY_PREVIEW_LIST,
    payload = null,
  } = preview ?? NO_PREVIEW;

  const hasPayload = !_.isNil(payload);
  const contextLine = getMersPreviewContextLine({ environment, channel, orgId });
  const verdictConfig = getMersPreviewVerdictConfig(verdict, hasPayload);

  const errorItems = useMemo(
    () => getMersPreviewErrorItems(errors ?? EMPTY_PREVIEW_LIST, message),
    [errors, message],
  );
  const fieldRows = useMemo(() => getMersPreviewFieldRows(fields ?? EMPTY_PREVIEW_LIST), [fields]);
  const runKey = useMemo(() => getMersPreviewRunKey(preview), [preview]);

  const onCopyPayload = useCallback(
    () =>
      copyToClipboard(payload).then(isCopied => {
        if (isCopied) {
          dispatch(
            showMessageAction({
              message: PAYLOAD_COPIED_MESSAGE,
              variant: 'success',
              withCloseButton: false,
            }),
          );
        }
      }),
    [dispatch, payload],
  );

  const verdictMessage = (
    <>
      {message}
      {!!errorItems.length && (
        <Box component="ul" data-testid={MersPreviewDrawerTestIds.ERRORS} sx={errorListStyles}>
          {errorItems.map(({ id, text }) => (
            <li key={id}>{text}</li>
          ))}
        </Box>
      )}
    </>
  );

  const content = (
    <Box sx={drawerBodyStyles}>
      {contextLine && (
        <Typography
          color="text.secondary"
          data-testid={MersPreviewDrawerTestIds.CONTEXT}
          variant="regular14"
        >
          {contextLine}
        </Typography>
      )}

      <InlineAlertBanner
        dataTestId={MersPreviewDrawerTestIds.VERDICT_BANNER}
        message={verdictMessage}
        title={verdictConfig.title}
        variant={verdictConfig.variant}
      />

      <Box data-testid={MersPreviewDrawerTestIds.FIELDS_TABLE}>
        <TableCDS
          isHideTableWhenEmpty
          config={MERS_PREVIEW_FIELDS_TABLE_CONFIG}
          data={fieldRows}
          rowTestId={MersPreviewDrawerTestIds.FIELDS_TABLE_ROW}
        />
      </Box>

      <MersPreviewPayload key={runKey} payload={payload} title={verdictConfig.payloadTitle} />
    </Box>
  );

  const actions = (
    <Button
      data-testid={MersPreviewDrawerTestIds.COPY_PAYLOAD_BUTTON}
      disabled={!hasPayload}
      variant="contained"
      onClick={onCopyPayload}
    >
      {COPY_PAYLOAD_LABEL}
    </Button>
  );

  return (
    <DrawerCDS
      actions={actions}
      cancelLabel={CLOSE_LABEL}
      content={content}
      dataTestId={MersPreviewDrawerTestIds.DRAWER}
      open={open}
      size={DrawerSize.LARGE}
      subtitle={NOTHING_SENT_SUBTITLE}
      title={getMersPreviewTitle(operation)}
      onClose={onClose}
    />
  );
};

MersPreviewDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  preview: mersPreviewPropType,
};

export default MersPreviewDrawer;
